import { Quadrant } from '../enums/Quadrant';
import { BalloonEventModel, MetadataModel, QueryModel } from '../models';
import { BalloonEventModelBuilder } from '../models/BalloonEventModel';
import { EventCollectionModel } from '../models/EventCollectionModel';
import { Fits } from '../models/QueryModel';
import { BalloonEventsQuery } from '../types';
import { ImageMetadata, ImageUploadData } from '../types/ImageSource';

export default abstract class BalloonEventsRepository {
  _balloonEventModelBuilder = new BalloonEventModelBuilder();
  _idToEvent = new Map<string, BalloonEventModel>();
  _slugToId = new Map<string, string>();
  _query: QueryModel = new QueryModel();
  _metadataModel: MetadataModel;
  _lastFetchAmount: number | null = null;

  constructor(metadata: MetadataModel) {
    this._metadataModel = metadata;
  }

  clear() {
    this._idToEvent.clear();
    this._slugToId.clear();
  }

  get lastFetchAmount() {
    return this._lastFetchAmount;
  }

  get eventMap() {
    return this._idToEvent;
  }

  get _metadata() {
    return this._metadataModel;
  }

  set metadata(metadata: MetadataModel) {
    this._metadataModel = metadata;
  }

  protected get _builder() {
    return this._balloonEventModelBuilder;
  }

  get query() {
    return this._query;
  }

  has(id: string) {
    return this._idToEvent.has(id) || this._slugToId.has(id);
  }

  get(id: string) {
    if (this._idToEvent.has(id)) {
      return this._idToEvent.get(id);
    } else if (this._slugToId.has(id)) {
      return this._idToEvent.get(this._slugToId.get(id)!);
    }
    return null;
  }

  async getAll(ids: string[]) {
    const missing = ids.filter((id) => !this._idToEvent.has(id));
    if (missing.length > 0) {
      const events = await this._fetchBalloonEvents(missing);
      for (const event of events) {
        this._upsert(event);
      }
    }
    return this.getAllSync(ids);
  }

  getAllSync(ids: string[]) {
    return ids.map((id) => this._idToEvent.get(id));
  }

  inject(data: BalloonEventModel[]) {
    for (const event of data) {
      this._upsert(event);
    }
  }

  get unsortedFiltered() {
    const filtered: BalloonEventModel[] = [];
    for (const event of this._idToEvent.values()) {
      if (this._query.fits(event) !== Fits.NO) {
        filtered.push(event);
      }
    }
    return filtered;
  }

  get events() {
    // Group events by quadrant (Fits type)
    const activeFits = new Map<
      Fits,
      { events: BalloonEventModel[]; taken: number }
    >();
    const pastFits = new Map<
      Fits,
      { events: BalloonEventModel[]; taken: number }
    >();
    const filtered: BalloonEventModel[] = [];
    const sliced: BalloonEventModel[] = [];

    for (const event of this._idToEvent.values()) {
      const fitType = this._query.fits(event);
      if (fitType === Fits.NO) continue;
      filtered.push(event);

      // Separate active/future (timeUntilStart !== null) from past (null)
      const isPast = event.timeUntilStart === null;
      const targetMap = isPast ? pastFits : activeFits;

      if (!targetMap.has(fitType)) {
        targetMap.set(fitType, { events: [event], taken: 0 });
      } else {
        targetMap.get(fitType)!.events.push(event);
      }
    }

    // Sort each bucket: closest to now first (using ?? to handle 0 correctly)
    const sortByClosest = (a: BalloonEventModel, b: BalloonEventModel) =>
      (b.timeUntilStart ?? Number.MAX_VALUE) -
      (a.timeUntilStart ?? Number.MAX_VALUE);

    for (const fit of activeFits.values()) {
      fit.events.sort(sortByClosest);
    }
    for (const fit of pastFits.values()) {
      fit.events.sort(sortByClosest);
    }

    // Track how many slots each quadrant has used (across both passes)
    const quadrantTaken = new Map<Fits, number>();

    // Pass 1: Take active/future events first (round-robin across quadrants)
    const activeKeys = new Map(activeFits);
    while (activeKeys.size > 0) {
      for (const [key, fit] of activeKeys) {
        const taken = quadrantTaken.get(key) ?? 0;
        if (fit.events.length > 0 && taken < this.query.amount) {
          sliced.push(fit.events.shift()!);
          quadrantTaken.set(key, taken + 1);
        } else {
          activeKeys.delete(key);
        }
      }
    }

    // Pass 2: Fill remaining slots with past events
    const pastKeys = new Map(pastFits);
    while (pastKeys.size > 0) {
      for (const [key, fit] of pastKeys) {
        const taken = quadrantTaken.get(key) ?? 0;
        if (fit.events.length > 0 && taken < this.query.amount) {
          sliced.push(fit.events.shift()!);
          quadrantTaken.set(key, taken + 1);
        } else {
          pastKeys.delete(key);
        }
      }
    }

    sliced.sort(
      (a, b) =>
        (a.timeUntilStart ?? Number.MAX_VALUE) -
        (b.timeUntilStart ?? Number.MAX_VALUE)
    );

    const minDateDistance =
      sliced.length > 0 ? sliced[0].timeUntilStart ?? 0 : 0;
    let maxDateDistance = minDateDistance;

    for (let index = sliced.length - 1; index >= 0; index--) {
      const event = sliced[index];
      if (event.timeUntilStart != null && event.timeUntilStart > maxDateDistance) {
        maxDateDistance = event.timeUntilStart;
      }
    }

    function normalizedSort(date: number) {
      if (date === maxDateDistance) return 1;
      if (maxDateDistance === minDateDistance) return 1;

      const distanceFromNow = Math.abs(date); // Absolute distance from now
      const minDistanceFromNow = Math.abs(maxDateDistance); // Closest event's distance
      const maxDistanceFromNow = Math.abs(minDateDistance); // Farthest event's distance

      const totalRange = maxDistanceFromNow - minDistanceFromNow;
      if (totalRange === 0) return 1;

      const normalizedDiff =
        (distanceFromNow - minDistanceFromNow) / totalRange;

      const exponent = 0.15; // Lower = more aggressive dropoff
      const result = 1 - Math.pow(normalizedDiff, exponent);
      return 0.5 + 0.5 * result;
    }

    for (const event of sliced) {
      event.sorting = Math.min(
        1,
        Math.max(0, normalizedSort(event.timeUntilStart ?? 0))
      );
    }

    sliced.reverse();

    return {
      filtered,
      sliced,
    };
  }

  public async loadQuery(cancel: boolean = true): Promise<Quadrant[]> {
    try {
      const events = await this._fetchBalloonEventsQuery(cancel);
      this._lastFetchAmount = events.length;
      if (!this.query.bbox || events.length < this.query.amount) {
        for (const event of events) {
          this._upsert(event);
        }
        return [];
      }
      const quadrants = new Int8Array(5);
      for (const event of events) {
        this._upsert(event);
        const quadrant = this.query.getQuadrant(event);
        if (quadrant !== -1) {
          quadrants[quadrant] += 1;
        }
      }
      return quadrants.reduce((acc, value, index) => {
        if (value === 0) {
          acc.push(index as Quadrant);
        }
        return acc;
      }, [] as Quadrant[]);
    } catch (e: any) {
      // Don't log abort errors as failures - they're expected when query params change
      if (e.name === 'AbortError') {
        console.log('[BalloonEventsRepository] Query aborted - new query in progress');
      } else {
        console.error('query failed', e);
      }
    }
    return [];
  }

  public async queryQuadrant(quadrant: Quadrant, cancel: boolean = true) {
    try {
      const events = await this._fetchBalloonEventsQuery(cancel, quadrant);
      for (const event of events) {
        this._upsert(event);
      }
    } catch (e: any) {
      console.error('query failed', e);
    }
  }

  public async load(
    id: string,
    invalidateChache: boolean = false
  ): Promise<BalloonEventModel | null> {
    try {
      const event = await this._fetchBalloonEvent(id, invalidateChache);
      this._upsert(event);
      return event;
    } catch (e: any) {
      console.error('event fetch failed', e);
      return null;
    }
  }

  public async loadAll(ids: string[]): Promise<BalloonEventModel[]> {
    try {
      const events = await this._fetchBalloonEvents(ids);
      for (const event of events) {
        this._upsert(event);
      }
      return events;
    } catch (e: any) {
      console.error('event fetch failed', e);
      return [];
    }
  }

  public async getFromQuery(
    query?: BalloonEventsQuery,
    cancel: boolean = false
  ) {
    if (query) {
      this._query.set(query);
    }
    const events = await this._fetchBalloonEventsQuery(cancel);
    for (const event of events) {
      this._upsert(event);
    }
    events.reverse();
    return events;
  }

  /**
   * Upper bound on cached events. JS Maps preserve insertion order, so we
   * FIFO-evict the oldest entries once the cap is exceeded. Re-upserting an
   * existing event re-inserts it at the back of the queue (LRU touch), so
   * events from the latest fetches always survive eviction. Sized to fit a
   * full clustering fetch (amount=2000) plus its quadrant cascade and a
   * bit of headroom — saved-search clustering paths with ~2000 events need
   * the whole set live so the supercluster count is accurate at the most
   * zoomed-out level.
   */
  private static readonly CACHE_CAP = 5000;

  protected _upsert(event: BalloonEventModel) {
    const existing = this._idToEvent.get(event.id);
    if (existing) {
      existing.update(event);
      // Move to the newest position so this event won't be next to evict.
      this._idToEvent.delete(event.id);
      this._idToEvent.set(event.id, existing);
      return;
    }
    this._idToEvent.set(event.id, event);
    this._slugToId.set(event.slug, event.id);

    while (this._idToEvent.size > BalloonEventsRepository.CACHE_CAP) {
      const oldestId = this._idToEvent.keys().next().value;
      if (!oldestId) break;
      const oldest = this._idToEvent.get(oldestId);
      this._idToEvent.delete(oldestId);
      // Only drop the slug mapping if it still points at the evicted id —
      // a slug collision (which the API shouldn't produce but cheaper to
      // guard than debug) must not leave stale entries dangling.
      if (oldest && this._slugToId.get(oldest.slug) === oldestId) {
        this._slugToId.delete(oldest.slug);
      }
    }
  }

  public abstract post(
    event: BalloonEventModel | EventCollectionModel,
    authToken: string
  ): Promise<{ status: number; data?: any }>;

  public abstract put(
    balloonId: string,
    event: BalloonEventModel,
    authToken: string
  ): Promise<{ status: number; data?: any }>;

  public abstract deleteBalloon(
    balloonEventId: string,
    authToken: string
  ): Promise<{ status: number; data?: any }>;

  public async uploadFiles(
    balloonId: string,
    authToken: string,
    uplodaData: ImageUploadData[]
  ): Promise<{ status: number; data?: any }[]> {
    return Promise.all(
      uplodaData.map((data) => this.uploadFile(balloonId, authToken, data))
    );
  }

  public abstract uploadFile(
    balloonId: string,
    authToken: string,
    uplodaData: ImageUploadData
  ): Promise<{ status: number; data?: any }>;

  public async rawQuery(
    query: BalloonEventsQuery,
    page: number = 1,
    abortController?: AbortController
  ) {
    const events = await this._rawQuery(
      query,
      page,
      abortController || new AbortController()
    );
    for (const event of events) {
      this._upsert(event);
    }
    events.reverse();
    return events;
  }

  protected abstract _fetchBalloonEventsQuery(
    cancel: boolean,
    quadrant?: number
  ): Promise<BalloonEventModel[]>;
  protected abstract _fetchBalloonEvents(
    ids: string[]
  ): Promise<BalloonEventModel[]>;
  protected abstract _fetchBalloonEvent(
    id: string,
    invalidateChache: boolean
  ): Promise<BalloonEventModel>;

  protected abstract _rawQuery(
    query: BalloonEventsQuery,
    page: number,
    abortController: AbortController
  ): Promise<BalloonEventModel[]>;
}
